import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../Context/Auth';
import axios from '../Config/axiosConfig';
import { avisosApi, erroAviso } from '../Shared/Utils/avisos';
import ModalAviso from './ModalAviso';

export default function AvisosCliente() {
  const { currentUser, isClient } = useAuth();
  const location = useLocation();
  const [avisos, setAvisos] = useState([]);
  const [erroConsulta, setErroConsulta] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const [dispensando, setDispensando] = useState(false);
  const [erroDispensa, setErroDispensa] = useState('');
  const dispensados = useRef(new Set());
  const ultimaConsulta = useRef(0);
  const diferencaServidor = useRef(0);
  const podeExibir = Boolean(isClient && currentUser?.token && location.pathname !== '/login');
  const usuario = currentUser?.userName;
  const token = currentUser?.token;
  const chave = `avisos-clientes:${usuario}`;

  useEffect(() => {
    setAvisos([]);
    setErroConsulta(false);
    setErroDispensa('');
    dispensados.current = new Set();
    if (!podeExibir) return undefined;
    try { dispensados.current = new Set(JSON.parse(sessionStorage.getItem(chave) || '[]')); } catch { /* Memória da sessão é suficiente se o armazenamento estiver indisponível. */ }
    let cancelado = false;
    let consultando = false;
    let controller;
    let repetir;
    const consultar = async () => {
      if (cancelado || consultando || document.visibilityState === 'hidden') return;
      clearTimeout(repetir);
      consultando = true;
      controller = new AbortController();
      try {
        const response = await axios.get(`${avisosApi}/ativos`, { signal: controller.signal });
        if (cancelado) return;
        setErroConsulta(false);
        const recebidos = response.data.avisos || [];
        const agora = new Date(response.data.agora).getTime();
        diferencaServidor.current = agora - Date.now();
        const validos = recebidos.filter(item => item.ativado && new Date(item.inicio).getTime() <= agora && new Date(item.fim).getTime() > agora);
        const pendentes = validos.filter(item => !dispensados.current.has(`${item.id}:${item.versao}`));
        setAvisos(pendentes);
        ultimaConsulta.current = Date.now();
      } catch (error) {
        if (!cancelado && !controller.signal.aborted) {
          setAvisos([]);
          setErroConsulta(erroAviso(error));
          console.error('Falha ao consultar avisos dos clientes.', { status: error.response?.status, message: error.message });
          repetir = setTimeout(consultar, 10000);
        }
      } finally { consultando = false; }
    };
    consultar();
    const timer = setInterval(consultar, 60000);
    const retomar = () => { if (Date.now() - ultimaConsulta.current > 5000) consultar(); };
    window.addEventListener('focus', retomar);
    document.addEventListener('visibilitychange', retomar);
    return () => {
      cancelado = true; controller?.abort(); clearInterval(timer); clearTimeout(repetir);
      window.removeEventListener('focus', retomar); document.removeEventListener('visibilitychange', retomar);
    };
  }, [podeExibir, usuario, token, chave, tentativa]);

  useEffect(() => {
    if (avisos.length === 0) return undefined;
    const proximoFim = Math.min(...avisos.map(item => new Date(item.fim).getTime()));
    const intervalo = Math.max(0, Math.min(proximoFim - Date.now() - diferencaServidor.current, 2147483647));
    const timer = setTimeout(() => {
      const agora = Date.now() + diferencaServidor.current;
      setAvisos(previous => previous.filter(item => new Date(item.fim).getTime() > agora));
    }, intervalo);
    return () => clearTimeout(timer);
  }, [avisos]);

  const fechar = () => {
    if (!avisos[0]) return;
    dispensados.current.add(`${avisos[0].id}:${avisos[0].versao}`);
    try { sessionStorage.setItem(chave, JSON.stringify([...dispensados.current])); } catch { /* Mantém a dispensa em memória. */ }
    setErroDispensa('');
    setAvisos(previous => previous.slice(1));
  };

  const naoExibirNovamente = async () => {
    const aviso = avisos[0];
    if (!aviso || dispensando) return;
    setDispensando(true);
    setErroDispensa('');
    try {
      await axios.post(`${avisosApi}/${aviso.id}/dispensar`, { versao: aviso.versao });
      fechar();
    } catch (error) {
      setErroDispensa(erroAviso(error));
    } finally { setDispensando(false); }
  };

  return <>
    {podeExibir && erroConsulta && <Alert variant="warning" className="m-3" role="alert">
      Não foi possível carregar os avisos. Você pode continuar usando o portal.
      {' '}{erroConsulta}
      {' '}<Button variant="link" onClick={() => setTentativa(previous => previous + 1)}>Tentar novamente</Button>
    </Alert>}
    <ModalAviso aviso={podeExibir ? avisos[0] : null} restantes={Math.max(0, avisos.length - 1)}
      onClose={fechar} onDismiss={naoExibirNovamente} dismissing={dispensando} dismissError={erroDispensa} />
  </>;
}
